/**
 * Video Detail Page
 * 
 * Displays a single video with player, metadata, and engagement options.
 * 
 * Route: /video/:videoId
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API, API_BASE_URL } from '../config/api';
import UserAvatar from '../components/UserAvatar';
import { getProfileUrl } from '../utils/profileUtils';
import { ShareButton } from '../components/ShareSheet';

import { 
  Play, Pause, Heart, Share2, ExternalLink, Video,
  ArrowLeft, Loader2, Clock, Eye, MessageCircle,
  Volume2, VolumeX, Maximize, Download, Calendar, User, Trophy
} from 'lucide-react';
import { RequestButton } from '../components/RequestButton';

export default function VideoPage() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [video, setVideo] = useState(null);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [viewCount, setViewCount] = useState(0);
  const [hasRequested, setHasRequested] = useState(false);
  const [requestingChart, setRequestingChart] = useState(false);
  
  const videoRef = useRef(null);

  useEffect(() => {
    fetchVideo();
  }, [videoId]);

  const fetchVideo = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const headers = isAuthenticated ? getAuthHeaders() : {};
      const res = await fetch(`${API}/videos/${videoId}`, { headers });
      
      if (!res.ok) {
        if (res.status === 404) {
          setError('Video not found');
        } else {
          setError('Failed to load video');
        }
        return;
      }
      
      const data = await res.json();
      console.log('[VideoPage] Video loaded:', data);
      setVideo(data);
      setIsLiked(data.is_liked || false);
      setLikeCount(data.likes || data.like_count || 0);
      setViewCount(data.views || data.view_count || data.plays || 0);
      
      // Record view
      try {
        await fetch(`${API}/videos/${videoId}/play`, {
          method: 'POST',
          headers
        });
      } catch (e) {
        console.log('[VideoPage] View count update failed:', e);
      }
    } catch (err) {
      console.error('[VideoPage] Fetch error:', err);
      setError('Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      toast.info('Please sign in to like videos');
      return;
    }
    
    try {
      const endpoint = isLiked ? 'unlike' : 'like';
      await fetch(`${API}/videos/${videoId}/${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      setIsLiked(!isLiked);
      setLikeCount(prev => isLiked ? prev - 1 : prev + 1);
    } catch (err) {
      toast.error('Failed to update like');
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: video?.title || 'Video',
          text: `Watch ${video?.title} by ${video?.artist_name || video?.creator_name}`,
          url: shareUrl
        });
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard');
    }
  };

  const handleRequestChart = async () => {
    if (!isAuthenticated) {
      toast.info('Please sign in to request videos for charts');
      navigate('/login');
      return;
    }
    
    setRequestingChart(true);
    try {
      const res = await fetch(`${API}/requests/video`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ video_id: videoId })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to request video');
      }
      
      setHasRequested(true);
      toast.success('Video requested for Top 20! Your support helps artists climb the charts.');
    } catch (err) {
      if (err.message.includes('Already requested')) {
        setHasRequested(true);
        toast.info('You already requested this video today');
      } else if (err.message.includes('Daily request limit')) {
        toast.error('Daily request limit reached (20/day)');
      } else {
        toast.error(err.message || 'Failed to request video');
      }
    } finally {
      setRequestingChart(false);
    }
  };

  // Get video URL
  const getVideoUrl = () => {
    if (!video) return null;
    
    if (video.video_url) {
      if (video.video_url.startsWith('/')) {
        return `${API_BASE_URL}${video.video_url}`;
      }
      return video.video_url;
    }
    
    if (video.url) {
      if (video.url.startsWith('/')) {
        return `${API_BASE_URL}${video.url}`;
      }
      return video.url;
    }
    
    return null;
  };

  const videoUrl = getVideoUrl();
  const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  
  // Try to get YouTube embed from any URL field
  const allUrls = [video?.youtube_url, video?.embed_url, video?.video_url, video?.external_url, video?.url].filter(Boolean);
  let youtubeEmbedUrl = null;
  let vimeoEmbedUrl = null;
  
  for (const url of allUrls) {
    const ytMatch = url.match(youtubeRegex);
    if (ytMatch) {
      youtubeEmbedUrl = `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
      break;
    }
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      vimeoEmbedUrl = `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
      break;
    }
  }
  
  // Determine if we can play in-app
  const canPlayInApp = youtubeEmbedUrl || vimeoEmbedUrl || videoUrl;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="glass-card border-border p-8 text-center max-w-md">
          <Video className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Video Unavailable</h1>
          <p className="text-muted-foreground mb-6">
            {error || "This video is no longer available or has been removed."}
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
            <Button onClick={() => navigate('/videos')}>
              Browse Videos
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <ShareButton
            url={`/video/${videoId}`}
            title={video?.title || 'Video'}
            description={`${video?.title} by ${video?.artist_name || 'artist'} on DMV Soundstage`}
          />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Video Player */}
        <div className="aspect-video bg-black rounded-xl overflow-hidden mb-6 relative" data-testid="video-player">
          {youtubeEmbedUrl ? (
            <iframe
              src={youtubeEmbedUrl}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          ) : vimeoEmbedUrl ? (
            <iframe
              src={vimeoEmbedUrl}
              title={video.title}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          ) : videoUrl ? (
            <video
              ref={videoRef}
              src={videoUrl}
              poster={video.thumbnail_url}
              controls
              playsInline
              className="w-full h-full"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center p-6">
                <Video className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-2">This video cannot be played in-app</p>
                {video.external_url && (
                  <>
                    <p className="text-xs text-muted-foreground mb-3">This video must open externally</p>
                    <Button
                      variant="outline"
                      onClick={() => window.open(video.external_url, '_blank')}
                      data-testid="external-video-btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open External Video
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Video Info */}
        <Card className="glass-card border-border p-6">
          <h1 className="text-2xl font-bold mb-3">{video.title}</h1>
          
          {/* Stats */}
          <div className="flex items-center gap-6 text-sm text-muted-foreground mb-4">
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {viewCount} views
            </span>
            <span className="flex items-center gap-1">
              <Heart className="w-4 h-4" />
              {likeCount} likes
            </span>
            {video.created_at && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(video.created_at).toLocaleDateString()}
              </span>
            )}
          </div>

          {/* Creator Info */}
          <div className="flex items-center justify-between border-t border-border pt-4">
            <Link 
              to={getProfileUrl(video)}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <UserAvatar 
                src={video.artist_avatar || video.creator_avatar} 
                alt={video.artist_name || video.creator_name}
                size="md"
              />
              <div>
                <p className="font-medium">{video.artist_name || video.creator_name || 'Unknown'}</p>
                {video.handle && (
                  <p className="text-sm text-muted-foreground">{video.handle ? `@${video.handle}` : ""}</p>
                )}
              </div>
            </Link>
            
            <div className="flex items-center gap-3">
              <Button 
                variant={isLiked ? "default" : "outline"}
                onClick={handleLike}
                className={isLiked ? "bg-red-500 hover:bg-red-600" : ""}
              >
                <Heart className={`w-4 h-4 mr-2 ${isLiked ? 'fill-current' : ''}`} />
                {isLiked ? 'Liked' : 'Like'}
              </Button>
              
              <Button variant="outline" onClick={handleShare}>
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              
              <Button 
                variant={hasRequested ? "secondary" : "default"}
                onClick={handleRequestChart}
                disabled={hasRequested || requestingChart}
                className={hasRequested ? "" : "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"}
              >
                {requestingChart ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trophy className="w-4 h-4 mr-2" />
                )}
                {hasRequested ? 'Requested' : 'Request Top 20'}
              </Button>

              {/* Request Button */}
              <RequestButton
                targetType="video"
                targetId={video.video_id}
                targetName={video.title}
                creatorName={video.artist_name || video.creator_name}
                variant="outline"
                size="default"
              />
            </div>
          </div>

          {/* Description */}
          {video.description && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-muted-foreground whitespace-pre-wrap">{video.description}</p>
            </div>
          )}

          {/* Tags */}
          {video.tags && video.tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {video.tags.map((tag, i) => (
                <Badge key={i} variant="outline">{tag}</Badge>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
